import {
  createContext,
  useReducer,
  useMemo,
  useCallback,
  useEffect,
  useContext,
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { MULTI_YEAR, ANNUAL_MULTI_YEAR } from '../constants/years';

const initialState = {
  crop: '',
  sectionGroupId: '',
  years: 30,
  year: MULTI_YEAR,
  // Note: Use shouldShowSubsidies from SubsidySwitchContext rather than
  // subsidiesEnabled directly for determining whether subsides should
  // be or will be applied. subsidiesEnabled here is scoped to the session
  // and may be true even when subsidies are not relevant for a particular view
  // and/or farm.
  subsidiesEnabled: false,
};

const FilterContext = createContext({
  state: initialState,
  updateFilter: () => {},
});

const reducer = (state, { type, filter, value }) => {
  if (state[filter] === value) return state;
  switch (type) {
    case 'update':
      return { ...state, [filter]: value };
    case 'updateSingleYear':
      // The specific year to display
      return { ...state, year: value, years: 1 };
    // Only one of years and year are used at a time.
    // If year is MULTI_YEAR we should look at years, if it is a specific year
    // years is unused (but set to 1 for clarity).
    case 'updateCumulativeMultiYear':
      // The number of years to display, starting at year 0.
      return { ...state, years: value, year: MULTI_YEAR };
    case 'updateAnnualMultiYear':
      return { ...state, years: value, year: ANNUAL_MULTI_YEAR };
    default:
      throw new Error(`Invalid action type: ${type}`);
  }
};

const setQueryParams = (location, navigate, values) => {
  const params = new URLSearchParams(location.search);
  Object.entries(values).forEach(([filter, value]) => {
    if (value === initialState[filter]) {
      params.delete(filter);
    } else {
      params.set(filter, value);
    }
  });
  const query = params.toString();
  navigate(`${location.pathname}${query ? `?${query}` : ''}${location.hash}`, {
    replace: true,
  });
};

// Merge initialState with any filters that are specified in the URL params
const getMergedInitialState = (location) => {
  const params = new URLSearchParams(location.search);
  return Object.keys(initialState).reduce(
    (res, filter) => {
      const value = params.get(filter);
      if (value === null) return res;
      if (typeof initialState[filter] === 'number') {
        res[filter] = parseInt(value, 10);
      } else if (typeof initialState[filter] === 'boolean') {
        res[filter] = value === 'true';
      } else {
        res[filter] = value;
      }
      return res;
    },
    { ...initialState },
  );
};

export const FilterContextProvider = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const mergedInitialState = useMemo(() => getMergedInitialState(location), []);

  const [state, dispatch] = useReducer(reducer, mergedInitialState);

  const updateFilter = useCallback((filter, value) => {
    dispatch({ filter, value, type: 'update' });
  }, []);

  const updateSingleYearFilter = useCallback((value) => {
    dispatch({ value, type: 'updateSingleYear' });
  }, []);

  const updateMultiYearFilter = useCallback((year, years) => {
    if (year !== MULTI_YEAR && year !== ANNUAL_MULTI_YEAR) {
      throw new Error(`Invalid year: ${year}`);
    }
    dispatch({
      value: years,
      type:
        year === MULTI_YEAR
          ? 'updateCumulativeMultiYear'
          : 'updateAnnualMultiYear',
    });
  }, []);

  // Set query params on state change
  useEffect(() => {
    setQueryParams(location, navigate, state);
  }, [state]); // eslint-disable-line react-hooks/exhaustive-deps

  const value = useMemo(
    () => ({
      state,
      updateFilter,
      updateSingleYearFilter,
      updateMultiYearFilter,
    }),
    [state, updateFilter, updateSingleYearFilter, updateMultiYearFilter],
  );
  return (
    <FilterContext.Provider value={value}>{children}</FilterContext.Provider>
  );
};

// Calling useFilterContext anywhere within a view will sync the
// current filter state back up to the URL.
export const useFilterContext = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const context = useContext(FilterContext);
  const { state } = context;

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    // Update the URL params when the path changes due to navigation
    // within the app. Putting this in useFilterContext ensures the URL params
    // are set at least once for views that use FilterContext, but are not
    // set for views where the params aren't relevant.
    setQueryParams(
      location,
      navigate,
      Object.keys(initialState)
        .filter((filter) => params.get(filter) !== state[filter])
        .reduce(
          (result, filter) =>
            Object.assign(result, { [filter]: state[filter] }),
          {},
        ),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  return context;
};
